import { blobEndpoint, datasetsContainer } from '~lib/storage/config';

const ABS_API_VERSION = '2024-08-04';

// Block size: 8 MiB is a sweet spot between request overhead and retry
// granularity for unreliable connections. The service maxes out at 4000 MiB
// per block; raising this past ~64 MiB rarely pays off in browsers.
const BLOCK_SIZE = 8 * 1024 * 1024;

// Above this threshold we switch from single PUT to block-list upload. Single
// PUT is capped at 5000 MiB by the service but, more importantly, can't be
// resumed or parallelised — block upload is strictly better past a few MiB.
const SINGLE_SHOT_THRESHOLD = 8 * 1024 * 1024;

const MAX_BLOCK_CONCURRENCY = 4;
const RETRY_ATTEMPTS = 3;

export interface UploadOptions {
  signal?: AbortSignal;
  onProgress?: (bytesUploaded: number) => void;
}

export async function uploadBlockBlob(
  token: string,
  fullPath: string,
  file: File,
  options: UploadOptions = {},
): Promise<void> {
  const url = `${blobEndpoint}/${datasetsContainer}/${encodeBlobPath(fullPath)}`;

  if (file.size <= SINGLE_SHOT_THRESHOLD) {
    await singleShotUpload(token, url, file, options);
    return;
  }

  await blockListUpload(token, url, file, options);
}

async function singleShotUpload(
  token: string,
  url: string,
  file: File,
  { signal, onProgress }: UploadOptions,
): Promise<void> {
  await withRetry(() =>
    abortable(signal, (controller) =>
      fetch(url, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'x-ms-version': ABS_API_VERSION,
          'x-ms-blob-type': 'BlockBlob',
          'x-ms-blob-content-type': file.type || 'application/octet-stream',
        },
        body: file,
        signal: controller.signal,
      }).then(throwIfNotOk),
    ),
  );
  onProgress?.(file.size);
}

async function blockListUpload(
  token: string,
  url: string,
  file: File,
  { signal, onProgress }: UploadOptions,
): Promise<void> {
  const blockCount = Math.ceil(file.size / BLOCK_SIZE);
  // Block IDs must be base64 strings of equal length within a single blob.
  // Pad the decimal index so every encoded ID is the same width.
  const padding = String(blockCount - 1).length;
  const blockIds = Array.from({ length: blockCount }, (_, i) =>
    btoa(String(i).padStart(padding, '0')),
  );

  let bytesUploaded = 0;
  const queue: number[] = Array.from({ length: blockCount }, (_, i) => i);
  const workers = Array.from(
    { length: Math.min(MAX_BLOCK_CONCURRENCY, blockCount) },
    async () => {
      for (;;) {
        const i = queue.shift();
        if (i === undefined) return;
        const start = i * BLOCK_SIZE;
        const end = Math.min(start + BLOCK_SIZE, file.size);
        const chunk = file.slice(start, end);
        await withRetry(() =>
          abortable(signal, (controller) =>
            fetch(`${url}?comp=block&blockid=${encodeURIComponent(blockIds[i])}`, {
              method: 'PUT',
              headers: {
                Authorization: `Bearer ${token}`,
                'x-ms-version': ABS_API_VERSION,
              },
              body: chunk,
              signal: controller.signal,
            }).then(throwIfNotOk),
          ),
        );
        bytesUploaded += chunk.size;
        onProgress?.(bytesUploaded);
      }
    },
  );

  await Promise.all(workers);

  const blockListXml =
    `<?xml version="1.0" encoding="utf-8"?><BlockList>` +
    blockIds.map((id) => `<Latest>${id}</Latest>`).join('') +
    `</BlockList>`;

  await withRetry(() =>
    abortable(signal, (controller) =>
      fetch(`${url}?comp=blocklist`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'x-ms-version': ABS_API_VERSION,
          'x-ms-blob-content-type': file.type || 'application/octet-stream',
          'Content-Type': 'application/xml',
        },
        body: blockListXml,
        signal: controller.signal,
      }).then(throwIfNotOk),
    ),
  );
}

async function throwIfNotOk(response: Response): Promise<Response> {
  if (response.ok) return response;
  const body = await response.text().catch(() => '');
  throw new Error(
    `${response.status} ${response.statusText}${
      body ? ` — ${body.slice(0, 200)}` : ''
    }`,
  );
}

// Wraps an async operation with retry-with-backoff. AbortError is never
// retried — that's a deliberate cancel.
async function withRetry<T>(operation: () => Promise<T>): Promise<T> {
  let lastError: unknown;
  for (let attempt = 0; attempt < RETRY_ATTEMPTS; attempt++) {
    try {
      return await operation();
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') throw e;
      lastError = e;
      // Exponential backoff: 250ms, 500ms, 1000ms
      await new Promise((resolve) => setTimeout(resolve, 250 * 2 ** attempt));
    }
  }
  throw lastError;
}

// Threads an external AbortSignal into a child controller per call so retries
// each get a fresh signal but external cancel still cuts through.
async function abortable<T>(
  external: AbortSignal | undefined,
  fn: (controller: AbortController) => Promise<T>,
): Promise<T> {
  const controller = new AbortController();
  if (external?.aborted) controller.abort();
  const onAbort = () => controller.abort();
  external?.addEventListener('abort', onAbort);
  try {
    return await fn(controller);
  } finally {
    external?.removeEventListener('abort', onAbort);
  }
}

function encodeBlobPath(path: string): string {
  return path.split('/').map(encodeURIComponent).join('/');
}
